import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty, reduce, values } from 'underscore';

import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { CheckInUser } from 'redux/schemas/api/in-person-event';
import { getInPersonEventConfirmations } from 'redux/actions/in-person-events';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import useInfiniteScroll from 'shared/hooks/use-infinite-scroll';
import usePrevious from 'shared/hooks/use-previous';
import UserListModalBody, { UserListEntry } from 'shared/components/user-list-modal-body';

type InPersonEventConfirmationsModalProps = {
  catalogId: string;
  sessionId: number;
  eventTitle: string;
  totalCount: number;
  show: boolean;
  onClose: () => void;
};

const InPersonEventConfirmationsModal = ({
  catalogId,
  sessionId,
  eventTitle,
  totalCount,
  show,
  onClose,
}: InPersonEventConfirmationsModalProps) => {
  const [confirmedUsers, setConfirmedUsers] = useState<Record<number, UserListEntry>>({});
  const [isPageFetching, setIsPageFetching] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [onceLoaded, setOnceLoaded] = useState(false);

  const dispatch = useAppDispatch();
  const prevPageNo = usePrevious(pageNo);
  const showRef = useRef(show);
  showRef.current = show;

  const hasMore = totalCount > values(confirmedUsers).length;

  const userTable = useRef<HTMLDivElement>(null);
  const isScrolledToBottom = useInfiniteScroll(userTable.current, 0);

  const fetchConfirmedUsers = useCallback(async (page: number) => {
    let result: CheckInUser[] = [];
    try {
      result = await dispatch(getInPersonEventConfirmations({ catalogId, sessionId, page, confirmed: true })).unwrap();
    } catch {
      setIsPageFetching(false);
      return;
    }
    // Ignore results if modal was closed during fetch
    if (!showRef.current) return;
    const userData = reduce(
      result,
      (users: Record<number, UserListEntry>, user: CheckInUser) => {
        users[user.id] = {
          id: user.id,
          fullName: `${user.firstName} ${user.lastName}`,
          email: user.email,
        };
        return users;
      },
      {},
    );
    setConfirmedUsers(prev => ({ ...prev, ...userData }));
    setOnceLoaded(true);
    setIsPageFetching(false);
  }, [catalogId, dispatch, sessionId]);

  useEffect(() => {
    if (!show) {
      setConfirmedUsers({});
      setPageNo(1);
      setOnceLoaded(false);
      setIsPageFetching(false);
    }
  }, [show]);

  useEffect(() => {
    if (show && isEmpty(confirmedUsers) && !onceLoaded && !isPageFetching) {
      if (totalCount === 0) {
        setOnceLoaded(true);
      } else {
        setIsPageFetching(true);
        fetchConfirmedUsers(pageNo);
      }
    }
  }, [fetchConfirmedUsers, confirmedUsers, onceLoaded, show, isPageFetching, pageNo, totalCount]);

  useEffect(() => {
    if (userTable.current && isScrolledToBottom && !isPageFetching && hasMore) {
      setPageNo(prev => prev + 1);
    }
  }, [hasMore, isPageFetching, isScrolledToBottom]);

  useEffect(() => {
    if (show && pageNo > 1 && pageNo !== prevPageNo && hasMore && !isPageFetching) {
      setIsPageFetching(true);
      fetchConfirmedUsers(pageNo);
    }
  }, [fetchConfirmedUsers, hasMore, pageNo, prevPageNo, show, isPageFetching]);

  return (
    <NvModal
      show={show}
      type={ModalType.DYNAMIC}
      header={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.VIEW_CONFIRMATION()}
      body={(
        <UserListModalBody
          title={eventTitle}
          countLabel={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CONFIRMATIONS(totalCount)}
          users={confirmedUsers}
          isLoaded={onceLoaded}
          isPageFetching={isPageFetching}
          userTableRef={userTable}
        />
      )}
      onClose={onClose}
      fullHeight={false}
    />
  );
};

export default InPersonEventConfirmationsModal;
